import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { useTranslate, useTranslationStatus } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const Prerogatives = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const { currentLanguage } = useLanguage();
  
  // Savoir si des traductions sont en cours
  const isTranslating = useTranslationStatus();

  // Traductions des textes statiques
  const { translatedText: titleTranslated } = useTranslate("Les prérogatives");
  const { translatedText: subtitleTranslated } = useTranslate("Les Lignages de Bruxelles");
  const { translatedText: descTranslated } = useTranslate("Découvrez les prérogatives des Lignages de Bruxelles");
  
  // Traduction du contenu dynamique - SEULEMENT si pageData.content existe
  const { translatedText: contentTranslated } = useTranslate(pageData?.content || "");

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      console.log('🔄 Chargement des données de la page...');
      const response = await fetch(`${API_BASE}/api/site/pages/prerogatives`);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Données chargées:', data);
        setPageData(data);
      }
    } catch (error) {
      console.error('Erreur chargement page:', error);
    } finally {
      setLoading(false);
    }
  };

  // Ne plus afficher de spinner bloquant - afficher la page directement
  return (
    <div className="min-h-screen bg-white">
      {/* Header Image Banner */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt={pageData.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        >
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      <div className="py-16" style={{ paddingTop: pageData?.header_image ? '4rem' : '8rem' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Alerte de traduction en cours - SOUS LE HEADER avec z-index élevé */}
          {isTranslating && currentLanguage !== 'fr' && (
            <div 
              className="mb-6 bg-blue-50 border-l-4 border-blue-600 text-blue-800 px-6 py-4 rounded-r-lg shadow-md flex items-center gap-3"
              style={{ fontFamily: '"Lora", serif', position: 'relative', zIndex: 10 }}
            >
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <div>
                <p className="font-semibold">Traduction en cours...</p>
                <p className="text-sm">Le contenu s'affiche en français en attendant la traduction complète.</p>
              </div>
            </div>
          )}
          
          {!pageData?.header_image && (
            <div className="text-center mb-12">
              <h1 
                className="text-5xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {titleTranslated}
              </h1>
              <p 
                className="text-gray-600"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {descTranslated}
              </p>
            </div>
          )}

          {/* Affichage du contenu dynamique depuis la DB */}
          {pageData?.content && (
            <div 
              dangerouslySetInnerHTML={{ 
                __html: currentLanguage === 'fr' 
                  ? pageData.content 
                  : (contentTranslated || pageData.content) 
              }}
              style={{ fontFamily: '"Lora", serif' }}
            />
          )}
          
          {/* Si pas de contenu, afficher un message */}
          {!pageData?.content && !loading && (
            <div className="text-center text-gray-500 py-12">
              <p style={{ fontFamily: '"Lora", serif' }}>Aucun contenu disponible pour cette page.</p>
            </div>
          )}

          {/* Contenu statique de secours si pas de contenu en DB */}
          {!pageData?.content && (
          <div className="space-y-8">
            {/* Introduction */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-3xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Administration des lignages
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-lg max-w-none">
                <p 
                  className="text-gray-700 leading-relaxed mb-4"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  A l'origine, la ville était administrée, sous le contrôle de l'Amman, officier du duc, par sept échevins 
                  appartenant aux lignages. Ce, dès le XIIIe siècle. À mesure que l'administration de la ville se complique, 
                  de nouvelles fonctions sont instituées qui déchargent les échevins d'une partie de leurs attributions.
                </p>
                <p 
                  className="text-gray-700 leading-relaxed mb-4"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Depuis 1421, les métiers groupés en « nations » sont associés aux lignages pour l'administration de 
                  la ville et les fonctions réparties, à peu près à égalité entre les lignages et les métiers, mais avec 
                  une certaine prédominance pour les premiers sauf à certaines époques où les finances dépendent 
                  davantage des métiers.
                </p>
              </CardContent>
            </Card>

            {/* Receveurs et trésoriers */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Receveurs et trésoriers (1334)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  En 1334, deux receveurs lignagers sont adjoints aux échevins et chargés de la recette et de la dépense 
                  de la ville et plus tard des travaux publics. En 1421 leur furent adjoints des receveurs des nations. 
                  Les receveurs des lignages prirent plus tard le nom de trésoriers.
                </p>
              </CardContent>
            </Card>

            {/* Apaiseurs */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Apaiseurs (1343)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  À partir de 1343, il fut nommé dix « apaiseurs » lignagers de concilier les cas de vengeances privées, 
                  d'imposer des sanctions aux offenses et plus tard de juger les affaires de querelles et de coups. 
                  En 1423, leur nombre est réduit à huit dont quatre lignagers.
                </p>
              </CardContent>
            </Card>

            {/* Conseillers patriciens */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Conseillers patriciens
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  En nombre variable, il y eut des conseillers patriciens, anciens échevins et anciens doyens de la gilde 
                  drapière, constituant l'arrière-conseil des échevins. Plus tard, avec les anciens bourgmestres et conseillers 
                  des nations, ils constituent ce qu'on appellera le Large Conseil ou deuxième Membre de la ville.
                </p>
              </CardContent>
            </Card>

            {/* Gilde drapière */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Gilde drapière (jusqu'en 1423)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Jusqu'en 1423, les chefs de la gilde drapière, administrateurs et juges des affaires de la draperie, 
                  étaient exclusivement lignagers. C'étaient deux doyens et huit assesseurs appelés les huit. À partir 
                  de cette date, la moitié de ces charges furent réservées aux nations.
                </p>
              </CardContent>
            </Card>

            {/* Bourgmestres */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Bourgmestres (1421)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  En 1421, lorsque les métiers obtinrent la participation à l'administration de la ville, on adjoignit 
                  aux sept échevins, six conseillers des nations. En outre, on créa deux charges de bourgmestres, 
                  l'un des lignages, l'autre des métiers. À l'origine les bourgmestres avaient des fonctions essentiellement 
                  judiciaires.
                </p>
              </CardContent>
            </Card>

            {/* Chefs tuteurs */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Chefs tuteurs (1445)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Les chefs tuteurs créés — semble-t-il — en 1445, chargés du contrôle des tutelles, étaient d'anciens 
                  bourgmestres, échevins et conseillers des nations. Les lignagers étaient de quatre sur six membres, 
                  dans ce collège.
                </p>
              </CardContent>
            </Card>

            {/* Maîtres de la charité */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Maîtres de la suprême charité (1539)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Les maîtres de la suprême charité, dont le statut fut fixé en 1539, étaient au nombre de quatre, 
                  dont deux lignagers, ils contrôlaient les institutions charitables.
                </p>
              </CardContent>
            </Card>

            {/* Surintendant du canal */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Surintendant du canal (1550)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  Le surintendant du canal, cette charge fut confiée à un lignager, Jean de Locquenqhien, en 1550, 
                  pour la direction et le contrôle des travaux du canal de Bruxelles à Boom, et il l'exerça jusqu'à sa 
                  mort en 1574. Cette fonction devint officiellement une charge lignagère en 1589. Le surintendant du 
                  canal avec deux receveurs, plus tard un seul, pris parmi les nations, était chargé de l'exploitation 
                  du canal.
                </p>
              </CardContent>
            </Card>

            {/* Garde bourgeoise */}
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-2xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  Garde bourgeoise (1421)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p 
                  className="text-gray-700 leading-relaxed"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  La garde bourgeoise de Bruxelles avait pour fonctions la défense des portes et remparts et le maintien 
                  de l'ordre à l'intérieur de la ville. Depuis 1591, la garde bourgeoise était commandée par dix capitaines. 
                  Les charges de capitaines de la garde bourgeoise, à partir de 1421, sont officiellement réservées aux 
                  lignagers. L'un des dix a le commandement en chef : c'est le major. Les cinq serments dont les chefs 
                  appartiennent aux nations participent aux mêmes fonctions que la garde bourgeoise.
                </p>
              </CardContent>
            </Card>

            {/* Image */}
            <div className="text-center mb-8">
              <img 
                src="/images/bruxelles-histoire.jpg" 
                alt="Histoire de Bruxelles"
                className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
              />
            </div>

            <Separator className="my-8" />

            {/* Citation finale */}
            <div className="text-center bg-gray-50 p-8 rounded-lg">
              <p 
                className="text-gray-700 italic text-xl leading-relaxed"
                style={{ fontFamily: '"Lora", serif' }}
              >
                « La ville de Bruxelles a su associer lignages et métiers pour gérer son administration et maintenir l'ordre, 
                répartissant les pouvoirs selon les époques et les besoins. »
              </p>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Prerogatives;